import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n/LangContext';
import { ArrowRight, Cpu, ChevronLeft, ChevronRight } from 'lucide-react';

const GALLERY = [
  { img: '/tech/01-robotic-tile-installation.webp', en: 'Robotic Tile Installation', es: 'Instalación Robótica de Azulejos' },
  { img: '/tech/02-autonomous-tile-grouting.webp', en: 'Autonomous Tile Grouting', es: 'Lechada de Azulejos Autónoma' },
  { img: '/tech/03-robotic-plastering-wall-finishing.webp', en: 'Robotic Plaster & Wall Finishing', es: 'Enlucido y Acabado Robótico' },
  { img: '/tech/04-autonomous-drywall-finishing.webp', en: 'Autonomous Drywall Finishing', es: 'Acabado Autónomo de Yeso' },
  { img: '/tech/05-digital-construction-layout.webp', en: 'Digital Construction Layout', es: 'Trazado Digital de Obra' },
  { img: '/tech/06-robotic-masonry.webp', en: 'Robotic Masonry', es: 'Mampostería Robótica' },
  { img: '/tech/07-robotic-inspection-spot.webp', en: 'AI-Powered Robotic Inspections', es: 'Inspecciones Robóticas con IA' },
];

const COPY = {
  en: {
    tag: 'New Initiative · Construction Technology',
    h: 'Construction Technology & Robotics Integration',
    sub: 'Bringing Robotics, Artificial Intelligence, and Advanced Project Management to Modern Construction.',
    p: "Dviso Consulting helps developers, property owners, and construction companies explore innovative robotic technologies through Owner's Representation, technology advisory, manufacturer coordination, and project implementation management.",
    btn: 'Explore Construction Technology',
    aiLabel: 'AI Concept',
  },
  es: {
    tag: 'Nueva Iniciativa · Tecnología de Construcción',
    h: 'Integración de Tecnología y Robótica en Construcción',
    sub: 'Robótica, Inteligencia Artificial y Gestión Avanzada de Proyectos para la Construcción Moderna.',
    p: 'Dviso Consulting ayuda a desarrolladores, propietarios y empresas constructoras a explorar tecnologías robóticas innovadoras mediante Representación del Propietario, asesoría tecnológica, coordinación con fabricantes y gestión de la implementación de proyectos.',
    btn: 'Explorar Tecnología de Construcción',
    aiLabel: 'Concepto IA',
  },
};

export default function TechPromo() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const trackRef = useRef(null);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('[data-tech-card]');
    const amount = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <section className="bg-[#0B0D0F] py-16 sm:py-24 border-t border-white/5" data-testid="home-tech-promo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-mono tracking-[0.2em] uppercase">
            <Cpu className="w-4 h-4" />{c.tag}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-outfit leading-tight">{c.h}</h2>
          <p className="text-lg text-[#D4AF37] mt-4 font-outfit">{c.sub}</p>
          <p className="text-slate-400 mt-4 leading-relaxed">{c.p}</p>
        </div>

        {/* Gallery */}
        <div className="relative mt-10">
          <div
            ref={trackRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="home-tech-gallery"
          >
            {GALLERY.map((g, i) => (
              <article
                key={i}
                data-tech-card
                data-testid={`home-tech-card-${i}`}
                className="group relative snap-start flex-shrink-0 w-[82%] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] rounded-xl overflow-hidden border border-white/10 bg-[#12151A] hover:border-[#D4AF37]/40 transition-all"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={g.img}
                    alt={`${g[lang]} — conceptual AI illustration`}
                    width="1448"
                    height="1086"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider bg-black/70 text-[#D4AF37] px-2 py-1 rounded">{c.aiLabel}</span>
                  <h3 className="absolute bottom-4 left-4 right-4 text-white font-semibold font-outfit text-lg leading-snug">{g[lang]}</h3>
                </div>
              </article>
            ))}
          </div>

          {/* Carousel controls */}
          <div className="flex items-center justify-between mt-5">
            <p className="text-xs text-slate-500 italic">
              {lang === 'es' ? 'Ilustraciones conceptuales generadas por IA.' : 'Conceptual, AI-generated illustrations.'}
            </p>
            <div className="flex gap-2">
              <button onClick={() => scrollBy(-1)} aria-label="Previous" data-testid="home-tech-prev" className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-all">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => scrollBy(1)} aria-label="Next" data-testid="home-tech-next" className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-all">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10">
          <Link
            to="/construction-technology-automation"
            data-testid="home-tech-promo-cta"
            className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-black font-bold px-8 py-4 rounded-lg transition-all"
          >
            {c.btn}<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
